import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { BLOGS_QUERY } from "../graphql/blogs";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  Stack,
  Divider,
  Skeleton,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { buildImageUrl } from "../config";

const formatDate = (iso?: string | null) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return "";
  }
};

export default function BlogsPage() {
  const { data, loading } = useQuery(BLOGS_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const navigate = useNavigate();

  const blogs = useMemo(() => {
    const list = (data?.blogs ?? []).slice();
    list.sort((a: any, b: any) => {
      const da = new Date(a?.publishedAt || a?.createdAt || 0).getTime();
      const db = new Date(b?.publishedAt || b?.createdAt || 0).getTime();
      return db - da;
    });
    return list;
  }, [data]);

  const featured = blogs[0];
  const rest = blogs.slice(1);

  return (
    <Box sx={{ py: { xs: 6, md: 9 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Stack
          spacing={1}
          alignItems="center"
          textAlign="center"
          sx={{ mb: 5 }}
        >
          <Typography
            variant="overline"
            sx={{ letterSpacing: 2, color: "text.secondary" }}
          >
            Journal
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
            Stories, guides & updates
          </Typography>
          <Typography
            sx={{
              maxWidth: 680,
              color: "text.secondary",
              fontSize: 16,
            }}
          >
            Thoughtfully written pieces on design, craftsmanship and everything
            we’re building.
          </Typography>
        </Stack>

        <Divider sx={{ mb: 4 }} />

        {/* Featured */}
        {loading ? (
          <Card sx={{ borderRadius: 0, overflow: "hidden", mb: 5 }}>
            <Grid container>
              <Grid item xs={12} md={7}>
                <Skeleton variant="rectangular" height={380} />
              </Grid>
              <Grid item xs={12} md={5}>
                <Box sx={{ p: 3 }}>
                  <Skeleton width="40%" />
                  <Skeleton width="90%" height={40} />
                  <Skeleton width="100%" />
                  <Skeleton width="95%" />
                  <Skeleton width="85%" />
                </Box>
              </Grid>
            </Grid>
          </Card>
        ) : featured ? (
          <Card
            sx={{
              borderRadius: 0,
              overflow: "hidden",
              mb: 5,
              cursor: "pointer",
              transition: "transform 220ms ease, box-shadow 220ms ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 18px 40px rgba(0,0,0,0.10)",
              },
            }}
            onClick={() => navigate(`/blogs/${featured.slug}`)}
          >
            <Grid container>
              <Grid item xs={12} md={7}>
                <CardMedia
                  component="img"
                  loading="lazy"
                  decoding="async"
                  image={
                    featured.coverImage
                      ? featured.coverImage.startsWith("http")
                        ? featured.coverImage
                        : buildImageUrl(featured.coverImage)
                      : "https://dummyimage.com/1200x800/eee/aaa"
                  }
                  alt={featured.title}
                  sx={{ height: { xs: 260, md: 380 }, objectFit: "cover" }}
                  onError={(e: any) => {
                    e.currentTarget.src =
                      "https://dummyimage.com/1200x800/eee/aaa";
                  }}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <Box sx={{ p: { xs: 3, md: 4 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label="Featured"
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(featured.publishedAt || featured.createdAt)}
                      </Typography>
                    </Stack>

                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, lineHeight: 1.2 }}
                    >
                      {featured.title}
                    </Typography>

                    <Typography
                      sx={{
                        color: "text.secondary",
                        display: "-webkit-box",
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {featured.excerpt || "Read the full story →"}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 1,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: 1.2,
                        fontSize: 12,
                      }}
                    >
                      Read article →
                    </Typography>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Card>
        ) : (
          <Typography color="text.secondary">No blogs found.</Typography>
        )}

        {/* Grid */}
        <Grid container spacing={3}>
          {(loading ? Array.from({ length: 6 }) : rest).map(
            (blog: any, idx: number) => (
              <Grid item xs={12} sm={6} md={4} key={blog?.id ?? idx}>
                {loading ? (
                  <Card sx={{ borderRadius: 0, overflow: "hidden" }}>
                    <Skeleton variant="rectangular" height={220} />
                    <Box sx={{ p: 2.5 }}>
                      <Skeleton width="40%" />
                      <Skeleton width="85%" height={34} />
                      <Skeleton width="100%" />
                      <Skeleton width="90%" />
                    </Box>
                  </Card>
                ) : (
                  <Card
                    sx={{
                      borderRadius: 0,
                      overflow: "hidden",
                      cursor: "pointer",
                      height: "100%",
                      transition: "transform 220ms ease, box-shadow 220ms ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 18px 40px rgba(0,0,0,0.10)",
                      },
                    }}
                    onClick={() => navigate(`/blogs/${blog.slug}`)}
                  >
                    <CardMedia
                      component="img"
                      loading="lazy"
                      decoding="async"
                      image={
                        blog.coverImage
                          ? buildImageUrl(blog.coverImage)
                          : "https://dummyimage.com/1200x800/eee/aaa"
                      }
                      alt={blog.title}
                      sx={{ height: 220, objectFit: "cover" }}
                    />

                    <Box sx={{ p: 2.5 }}>
                      <Stack spacing={1.2}>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(blog.publishedAt || blog.createdAt)}
                        </Typography>

                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            lineHeight: 1.25,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {blog.title}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {blog.excerpt || "Read the full story →"}
                        </Typography>
                      </Stack>
                    </Box>
                  </Card>
                )}
              </Grid>
            ),
          )}
        </Grid>
      </Container>
    </Box>
  );
}
